<template>
    <div class="checkout-ferry-pax">
        <div class="checkout-ferry-pax-form-header">
            <div class="checkout-ferry-pax-form-header-title">
                <Translation
                    element="strong"
                    :translate="{
                        key: 'shared.traveller',
                        format: LabelFormat.Capitalize,
                    }" />
            </div>
            <div class="checkout-ferry-pax-form-header-type">
                {{ travellerType }}
            </div>
        </div>

        <div class="checkout-ferry-pax-fields">
            <div v-if="gender" class="checkout-ferry-pax-fields-genders">
                <div class="checkout-ferry-pax-fields-genders-label">
                    {{ t({ key: 'shared.gender' }) }}
                </div>
                <RadioField
                    v-model="pax.Gender"
                    :name="gender.name"
                    :input="{ items: genderItems, inline: true }" />
            </div>

            <TextboxField
                v-if="firstName"
                v-model="pax.FirstName"
                :name="firstName.name"
                :input="{
                    label: t({
                        key: 'checkout.first_name_placeholder_label',
                    }),
                    capitalize: true,
                    autocomplete: 'given-name',
                }" />
            <TextboxField
                v-if="lastName"
                v-model="pax.LastName"
                :name="lastName.name"
                :input="{
                    label: t({
                        key: 'checkout.last_name_placeholder_label',
                    }),
                    capitalize: true,
                    autocomplete: 'family-name',
                }" />
            <DateInputField
                v-if="dateOfBirth"
                v-model="pax.DateOfBirth"
                :name="dateOfBirth.name"
                :input="{
                    label: t({ key: 'shared.date_of_birth' }),
                    autocomplete: 'bday',
                }" />

            <SelectField
                v-if="nationality"
                v-model="pax.Nationality"
                :name="nationality.name"
                :input="{
                    label: t({ key: 'shared.nationality' }),
                    items: nationalityItems,
                }" />
            <DateInputField
                v-if="passportExpirationDate"
                v-model="pax.PassportExpirationDate"
                :name="passportExpirationDate.name"
                :input="{
                    label: t({ key: 'shared.passport_expiration_date' }),
                }" />
            <TextboxField
                v-if="passportNumber"
                v-model="pax.PassportNumber"
                :name="passportNumber.name"
                :input="{
                    label: t({ key: 'shared.passport_number' }),
                    capitalize: true,
                }" />
            <SelectField
                v-if="specialNeeds"
                v-model="pax.SpecialNeedsOption"
                :name="specialNeeds.name"
                :input="{
                    label: t({ key: 'checkout.special_needs' }),
                    items: specialNeedsItems,
                }" />
            <TextboxField
                v-if="loyaltyMembership"
                v-model="pax.LoyaltyMembership"
                :name="loyaltyMembership.name"
                :input="{
                    label: t({
                        key: 'checkout.stena_loyalty_membership_label',
                    }),
                    placeholder: '1234567',
                    hint: t({
                        key: 'checkout.number_field_hint',
                        values: ['1234567'],
                    }),
                    tooltip: t({
                        key: 'checkout.stena_loyalty_membership_text',
                    }),
                }" />
        </div>
        <Divider color="lightGrey" thickness="1" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import {
        FerryPax,
        FerryProduct,
        MandatoryField,
        Nationality,
    } from '@/api/Cart/cart.interfaces';
    import { useStore } from '@/store';
    import {
        AgeRangeDefinitions,
        SpecialNeedsOption,
    } from '@/api/Checkout/checkout.interfaces';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
    import { getGendersByProductAndPax } from '@/helpers/checkout';
    import { getPaxFields } from '@/components/checkoutPageV3/Sections/Products/Ferry/Form/FerryPax.helpers';
    import { getCapitalizedCase } from '@shared/utils/string';
    import { getPaxSubHeader } from '@/helpers/guests';
    import { configBase } from '@/config/base';
    import RadioField from '@ui/components/FormV2/Fields/Radio/RadioField.vue';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import SelectField from '@ui/components/FormV2/Fields/Select/SelectField.vue';
    import Translation from '@ui/components/Translation/Translation.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    export default defineComponent({
        name: 'Pax',
        methods: { t },
        components: {
            Translation,
            SelectField,
            DateInputField,
            TextboxField,
            RadioField,
            Divider,
        },
        props: {
            index: { type: Number, required: true },
            pax: { type: Object as PropType<FerryPax>, required: true },
            product: {
                type: Object as PropType<FerryProduct>,
                required: true,
            },
            ageRangeDefinitions: {
                type: Object as PropType<AgeRangeDefinitions>,
                required: true,
            },
            toDate: {
                type: Object as PropType<Date>,
                required: true,
            },
        },
        setup(props) {
            const { smallBagIncluded } = configBase;

            const store = useStore();

            const fields = computed(() => getPaxFields({ ...props }));

            const getField = (field: MandatoryField) =>
                fields.value.find((f) => f.field === field);

            const gender = computed(() => getField(MandatoryField.Gender));

            const firstName = computed(() => getField(MandatoryField.FirstName));

            const lastName = computed(() => getField(MandatoryField.LastName));

            const dateOfBirth = computed(() => getField(MandatoryField.DateOfBirth));

            const nationality = computed(() => getField(MandatoryField.Nationality));

            const passportExpirationDate = computed(() =>
                getField(MandatoryField.PassportExpirationDate)
            );
            const passportNumber = computed(() => getField(MandatoryField.PassportNumber));
            const specialNeeds = computed(() => getField(MandatoryField.SpecialNeeds));

            const loyaltyMembership = computed(() =>
                getField(MandatoryField.LoyaltyMembership)
            );

            const ageRangeDefinitions = computed<AgeRangeDefinitions>(() => {
                return store.getters['content/getCheckoutAgeRangeDefinitions'];
            });

            const genderItems = computed(() =>
                getGendersByProductAndPax(props.product, props.pax)
            );

            const nationalityItems = computed<SelectItem[]>(() => {
                const nationalities: Nationality[] =
                    store.getters['content/getCheckoutNationalities'];

                return nationalities.length
                    ? nationalities.map((nationality) => ({
                          label: nationality.NationalityName,
                          value: nationality,
                      }))
                    : [];
            });

            const specialNeedsItems = computed<SelectItem[]>(() => {
                const specialNeeds: SpecialNeedsOption[] =
                    store.getters['content/getCheckoutSpecialNeedsOptions'];

                return specialNeeds.length
                    ? specialNeeds.map((specialNeed) => ({
                          label: specialNeed.Name,
                          value: specialNeed.Value,
                      }))
                    : [];
            });

            const travellerType = computed(() => {
                if (props.pax.PaxDescription) {
                    const label = getCapitalizedCase(
                        getPaxSubHeader(
                            ageRangeDefinitions.value,
                            props.product.Type,
                            props.pax.PaxDescription.Age
                        )
                    );
                    return label;
                }

                return undefined;
            });

            return {
                travellerType,
                smallBagIncluded,
                genderItems,
                nationalityItems,
                gender,
                firstName,
                lastName,
                dateOfBirth,
                nationality,
                passportExpirationDate,
                passportNumber,
                specialNeeds,
                loyaltyMembership,
                specialNeedsItems,
                LabelFormat,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Pax.scss';
</style>
